import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";

import { Box, useMediaQuery, useTheme } from "@mui/material";

import { SidePanel } from "./SidePanel";
import { type HeaderTabConfig } from "./types";

type Props = {
  open: boolean;
  onClose: () => void;
  panelContent: ReactNode;
  panelTitle?: string;
  dataCy?: string;
  headerElement?: ReactNode;
  headerTabs?: HeaderTabConfig;
  children: ReactNode;
  loading?: boolean;
  closeOnSmallScreen?: boolean;
  responsiveBreakpoint?: "sm" | "md" | "lg" | "xl";
  useSidePanelFullHeight?: boolean;
};

export const SlidingPanel = ({
  open,
  onClose,
  panelContent,
  panelTitle,
  dataCy,
  headerElement,
  headerTabs,
  children,
  loading,
  closeOnSmallScreen = false,
  responsiveBreakpoint = "lg",
  useSidePanelFullHeight = false,
}: Props) => {
  const theme = useTheme();
  const isLargeEnoughScreen = useMediaQuery(theme.breakpoints.up(responsiveBreakpoint));
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [mainContentHeight, setMainContentHeight] = useState<number | null>(null);

  // Auto-close if enabled
  useEffect(() => {
    if (closeOnSmallScreen && open && !isLargeEnoughScreen) {
      onClose();
    }
  }, [closeOnSmallScreen, isLargeEnoughScreen, open, onClose]);

  useLayoutEffect(() => {
    if (open && mainContentRef.current) {
      const updateHeight = () => {
        if (mainContentRef.current) {
          const height = mainContentRef.current.offsetHeight;
          setMainContentHeight(useSidePanelFullHeight ? window.innerHeight * 0.9 : height);
        }
      };

      updateHeight();

      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(mainContentRef.current);

      window.addEventListener("resize", updateHeight);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", updateHeight);
      };
    }
  }, [open, useSidePanelFullHeight]);

  return (
    <Box>
      <Box sx={{ display: "flex", width: "100%", gap: open ? 2 : 0, alignItems: "flex-start" }}>
        <Box ref={mainContentRef} sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
          {children}
        </Box>
        <SidePanel
          open={open}
          onClose={onClose}
          title={panelTitle}
          dataCy={dataCy}
          headerElement={headerElement}
          headerTabs={headerTabs}
          loading={loading}
          maxHeight={mainContentHeight}
        >
          {panelContent}
        </SidePanel>
      </Box>
    </Box>
  );
};

export default SlidingPanel;
